/**
 * wall-art-picker.js
 *
 * Custom element <wall-art-picker> driving the format/size/frame_color picker
 * for the single-variant storefront product architecture.
 *
 * Reads:
 *   - shop.metafields.wall_art_config.pricing_variant_map (embedded JSON inside
 *     the element by wall-art-picker.liquid)
 *   - design_id / design_handle / design_name / design_image (data-* attributes)
 *
 * Writes:
 *   - The product form's hidden <input name="id"> → resolved Price Map variant id
 *   - Hidden <input name="properties[_design_id]"> + visible properties
 *     appended to the form so cart/add.js receives them
 *   - Live price text in the closest .price element on the page
 *
 * Emits custom DOM events the wall-art-gallery-renderer.js subscribes to:
 *   - 'wallart:selection:changed' { format, size, frameColor, variantId, price }
 */

if (!customElements.get('wall-art-picker')) {
  class WallArtPicker extends HTMLElement {
    connectedCallback() {
      this.parsePricingMap();
      if (!this.pricingMap) return; // bootstrap not run; legacy banner shown server-side

      this.designContext = {
        designId: this.dataset.designId,
        designUuid: this.dataset.designUuid || '',  // internal UUIDv4, may be blank if metafield unset
        designHandle: this.dataset.designHandle,
        designName: this.dataset.designName,
        designImage: this.dataset.designImage,
      };

      this.state = { format: null, size: null, frameColor: null };

      this.formatButtons = Array.from(this.querySelectorAll('[data-wap-format]'));
      this.sizePillsHost = this.querySelector('[data-wap-size-pills]');
      this.sizeSelectedLabel = this.querySelector('[data-wap-size-selected-label]');
      this.framePicker = this.querySelector('[data-wap-frame-picker]');
      this.frameSwatchesHost = this.querySelector('[data-wap-frame-swatches]');
      this.frameSelectedLabel = this.querySelector('[data-wap-frame-selected-label]');

      this.formId = this.dataset.productFormId;
      this.form = this.formId ? document.getElementById(this.formId) : null;
      if (!this.form) {
        console.warn('[wall-art-picker] product form not found:', this.formId);
      }

      this.buildIndex();
      this.bindEvents();

      // Resolve the initial selection in priority order:
      //   1. A shared ?wa_format/?wa_size/?wa_frame URL (explicit intent — a
      //      copied share link or an ad/feed deep-link; always wins so the
      //      landing price matches the link).
      //   2. The customer's last format/size/frame, persisted across product
      //      navigation, so browsing product→product keeps their choice
      //      instead of resetting to the 24x36 Art Print default.
      //   3. The catalog default (first format → 24x36 → first available).
      // selectFormat's existing validity checks + fallbacks cleanly handle a
      // stored combo that a given design doesn't offer.
      const urlSelection = this.readSelectionFromUrl();
      const hasUrlSelection = !!(urlSelection.format || urlSelection.size || urlSelection.frame);
      const storedSelection = hasUrlSelection ? {} : this.readStoredSelection();
      const preferred = {
        format: urlSelection.format || storedSelection.format,
        size:   urlSelection.size   || storedSelection.size,
        frame:  urlSelection.frame  || storedSelection.frame,
      };

      const initialFormat =
        (preferred.format && this.formatIndex[preferred.format])
          ? preferred.format
          : Object.keys(this.formatIndex)[0];

      if (initialFormat) {
        // Stash the pending size/frame BEFORE selectFormat runs so its
        // size/frame resolution can prefer these over the defaults.
        this.pendingUrlSize = preferred.size;
        this.pendingUrlFrame = preferred.frame;
        this.selectFormat(initialFormat);
        // Clear after consumption — subsequent format clicks fall back to
        // their own preserve-prior-selection logic.
        this.pendingUrlSize = null;
        this.pendingUrlFrame = null;
      }

      // ── Conversion tracking: fire view_item once on initial paint ──
      // (Phase D.2). Subsequent picker changes do NOT re-fire view_item;
      // Google + Klaviyo + Pinterest treat view_item as a page-level
      // signal and re-firing would inflate engagement counts. The
      // selected format/size/frame at first paint is what gets
      // attributed — which is correct because that's what the customer
      // viewed when they arrived. Skipped silently if the conversion-
      // tracker module hasn't loaded yet (its <script> is `defer`ed
      // alongside ours, but order can vary).
      this.fireViewItem();

      // Subscribe to product-form's cart-update pub/sub so we can fire
      // add_to_cart with the picker's full context (variant, design uuid,
      // size, frame) after a successful /cart/add.js. product-form.js
      // emits { source: 'product-form', productVariantId, cartData }.
      // `subscribe` + `PUB_SUB_EVENTS` are globals declared by pubsub.js
      // + constants.js (loaded before this script via theme.liquid).
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
        this._cartUpdateUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          // Defensive: cart-update can fire from cart-drawer / cart.js
          // too. Only react to product-form's emits.
          if (!event || event.source !== 'product-form') return;
          this.fireAddToCart(event);
        });
      }
    }

    disconnectedCallback() {
      if (this._cartUpdateUnsub) {
        this._cartUpdateUnsub();
        this._cartUpdateUnsub = null;
      }
    }

    /** Parse the picker selection out of the current URL's query string.
     *  Params are namespaced `wa_*` to avoid Shopify-reserved names
     *  (`format`, `size`, `view`, `variant`, `currency`, etc. — some of
     *  these are intercepted by Shopify's storefront router and cause
     *  product pages to 404). Returns `{ format, size, frame }` with each
     *  field being a string when present + non-empty, otherwise undefined.
     *  We don't validate values here — `selectFormat` walks the pricing
     *  map and falls back cleanly when a URL param doesn't match. */
    readSelectionFromUrl() {
      try {
        const u = new URL(window.location.href);
        const get = (k) => {
          const v = u.searchParams.get(k);
          return v && v.trim() ? v.trim() : undefined;
        };
        return {
          format: get('wa_format'),
          size:   get('wa_size'),
          frame:  get('wa_frame'),
        };
      } catch {
        return {};
      }
    }

    /** Write the current selection to the URL query string via
     *  `history.replaceState` (no new history entry — back button still
     *  exits to the previous page, not through every variant change). */
    writeSelectionToUrl() {
      const { format, size, frameColor } = this.state;
      if (!format || !size) return;
      try {
        const u = new URL(window.location.href);
        u.searchParams.set('wa_format', format);
        u.searchParams.set('wa_size', size);
        if (frameColor && frameColor !== 'none') {
          u.searchParams.set('wa_frame', frameColor);
        } else {
          u.searchParams.delete('wa_frame');
        }
        // Strip the unprefixed legacy names if they snuck in via an old
        // shared URL (so we don't end up with both old + new in the bar).
        u.searchParams.delete('format');
        u.searchParams.delete('size');
        u.searchParams.delete('frame');
        // Drop Shopify's native ?variant=… — for our store every design
        // product has only one (placeholder) variant so it carries no
        // useful information and clutters the URL.
        u.searchParams.delete('variant');
        history.replaceState(null, '', u.toString());
      } catch {
        // ignore — URL update is non-critical
      }
    }

    /** Read the customer's last persisted format/size/frame selection so it
     *  carries across product navigation (and tabs/visits). Shaped like
     *  readSelectionFromUrl() — { format, size, frame } — so the two merge
     *  cleanly. localStorage access is wrapped: it can throw in private mode
     *  or when storage is disabled. */
    readStoredSelection() {
      try {
        const raw = window.localStorage.getItem('wallArtSelection');
        if (!raw) return {};
        const o = JSON.parse(raw) || {};
        return {
          format: o.format || undefined,
          size: o.size || undefined,
          frame: (o.frameColor && o.frameColor !== 'none') ? o.frameColor : undefined,
        };
      } catch {
        return {};
      }
    }

    /** Persist the committed selection so the next product page restores it
     *  instead of snapping back to the catalog default. */
    writeStoredSelection() {
      try {
        const { format, size, frameColor } = this.state;
        if (!format || !size) return;
        window.localStorage.setItem(
          'wallArtSelection',
          JSON.stringify({ format, size, frameColor })
        );
      } catch {
        // storage disabled / over quota — non-critical
      }
    }

    parsePricingMap() {
      const node = this.querySelector('[data-wap-pricing-map]');
      if (!node) return;
      try {
        const raw = node.textContent.trim();
        // The metafield can come back as a JSON-encoded string or as a
        // pre-parsed object embedded by Liquid's `| json` filter.
        const parsed = JSON.parse(raw);
        this.pricingMap = typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
      } catch (err) {
        console.error('[wall-art-picker] failed to parse pricing_variant_map:', err);
      }
    }

    /**
     * Build a quick lookup index from the flat pricing_variant_map keys.
     *   formatIndex: { format -> { size -> { frame_color -> { variant_id, price } } } }
     */
    buildIndex() {
      this.formatIndex = {};
      for (const [key, value] of Object.entries(this.pricingMap)) {
        const [format, size, frameColor] = key.split('|');
        if (!this.formatIndex[format]) this.formatIndex[format] = {};
        if (!this.formatIndex[format][size]) this.formatIndex[format][size] = {};
        this.formatIndex[format][size][frameColor] = value;
      }
    }

    bindEvents() {
      for (const btn of this.formatButtons) {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.selectFormat(btn.dataset.wapFormat);
        });
      }
      if (this.sizePillsHost) {
        // Delegated click — pill buttons are dynamically inserted whenever
        // the format changes, so listening on the host once covers all of
        // them.
        this.sizePillsHost.addEventListener('click', (e) => {
          const target = e.target.closest('[data-wap-size]');
          if (!target) return;
          this.selectSize(target.dataset.wapSize);
        });
      }
      if (this.frameSwatchesHost) {
        this.frameSwatchesHost.addEventListener('click', (e) => {
          const target = e.target.closest('[data-wap-frame]');
          if (!target) return;
          this.selectFrameColor(target.dataset.wapFrame);
        });
      }
    }

    // ─── Selection ──────────────────────────────────────────────────────

    selectFormat(format) {
      if (!this.formatIndex[format]) {
        // Format not available for this design (shouldn't happen in normal
        // usage since every design has all 4 formats). Disable button.
        return;
      }

      // Preserve the customer's prior size selection if the new format also
      // offers it. Otherwise fall back to 24x36 (every format in our catalog
      // has this size — it's the bestseller default). If neither, fall back
      // to the first available size for the format.
      const previousSize = this.state.size;
      const previousFrameColor = this.state.frameColor;

      this.state.format = format;
      this.state.size = null;
      this.state.frameColor = null;

      // Update format button visual state.
      for (const btn of this.formatButtons) {
        const isActive = btn.dataset.wapFormat === format;
        btn.classList.toggle('product-type-switcher__item--active', isActive);
        btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
      }

      // Repopulate sizes for the chosen format as pill buttons.
      const sizes = Object.keys(this.formatIndex[format]);
      this.renderSizePills(sizes);

      // Show/hide frame color picker based on whether this format has frames.
      const sampleSize = sizes[0];
      const frameColors = sampleSize ? Object.keys(this.formatIndex[format][sampleSize]) : [];
      const hasFrames = frameColors.length > 0 && !(frameColors.length === 1 && frameColors[0] === 'none');
      this.framePicker.hidden = !hasFrames;

      if (hasFrames) {
        this.renderFrameSwatches(frameColors);
      } else {
        // Auto-select the implicit "none" frame.
        this.state.frameColor = 'none';
      }

      // Resolve the size to keep:
      //   1. URL pending value (one-shot at boot, from ?size=…)
      //   2. Previous customer selection if the new format also has it
      //   3. 24x36 (catalog bestseller default)
      //   4. First available
      let resolvedSize = null;
      if (this.pendingUrlSize && sizes.includes(this.pendingUrlSize)) {
        resolvedSize = this.pendingUrlSize;
      } else if (previousSize && sizes.includes(previousSize)) {
        resolvedSize = previousSize;
      } else if (sizes.includes('24x36')) {
        resolvedSize = '24x36';
      } else if (sizes.length > 0) {
        resolvedSize = sizes[0];
      }

      // Resolve the frame color to keep:
      //   1. URL pending value (?frame=…)
      //   2. Previous customer selection if the new format also has it
      //   3. First available frame
      //   4. 'none' (unframed format)
      if (hasFrames) {
        if (this.pendingUrlFrame && frameColors.includes(this.pendingUrlFrame)) {
          this.state.frameColor = this.pendingUrlFrame;
        } else if (previousFrameColor && previousFrameColor !== 'none' && frameColors.includes(previousFrameColor)) {
          this.state.frameColor = previousFrameColor;
        } else {
          this.state.frameColor = frameColors[0];
        }
      }

      // Reflect the resolved size by marking the matching pill selected,
      // then commit so gallery + features re-render automatically (no extra
      // customer click required).
      if (resolvedSize) {
        this.markSelectedSizePill(resolvedSize);
        this.selectSize(resolvedSize);
      } else {
        this.commit();
      }
    }

    /** Pretty-print a size token for display. Keep the underlying
     *  state in the canonical snake-case form (`24x36`) so registry
     *  keys / URLs / AR variant lookups stay matchable, but render to
     *  the customer with explicit inch marks (`24"x36"`). No-op for
     *  tokens that don't match the WxH pattern. */
    static formatSizeDisplay(size) {
      if (!size) return '';
      const m = String(size).match(/^(\d+)x(\d+)$/);
      return m ? `${m[1]}"x${m[2]}"` : String(size);
    }

    renderSizePills(sizes) {
      if (!this.sizePillsHost) return;
      this.sizePillsHost.innerHTML = '';
      for (const size of sizes) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'wap-size-pill';
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', 'false');
        btn.dataset.wapSize = size;  // canonical token — never touch
        btn.textContent = WallArtPicker.formatSizeDisplay(size);
        this.sizePillsHost.appendChild(btn);
      }
    }

    markSelectedSizePill(size) {
      if (!this.sizePillsHost) return;
      for (const btn of this.sizePillsHost.querySelectorAll('[data-wap-size]')) {
        const isActive = btn.dataset.wapSize === size;
        btn.classList.toggle('wap-size-pill--selected', isActive);
        btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
      }
      if (this.sizeSelectedLabel) {
        this.sizeSelectedLabel.textContent = WallArtPicker.formatSizeDisplay(size);
      }
      this.scrollSelectedSizePillIntoView(size);
    }

    /** Keep the selected size pill visible inside the internally-scrollable
     *  pills row (mobile, `[data-wap-size-pills] { overflow-x: auto }`). On first
     *  paint the preselected size (e.g. 24"x36") can sit far right and start
     *  off-screen; this nudges ONLY the pills row horizontally to reveal it —
     *  never the page — and no-ops when the pill is already fully visible (so a
     *  customer's own tap never jumps the row under their finger; desktop, where
     *  pills wrap and there's no scroll, is unaffected). */
    scrollSelectedSizePillIntoView(size) {
      const host = this.sizePillsHost;
      if (!host || !size) return;
      const sel = (window.CSS && CSS.escape) ? CSS.escape(size) : size;
      const pill = host.querySelector(`[data-wap-size="${sel}"]`);
      if (!pill) return;
      // Defer one frame so layout (clientWidth/scrollWidth) is settled at boot.
      requestAnimationFrame(() => {
        const hostRect = host.getBoundingClientRect();
        const pillRect = pill.getBoundingClientRect();
        if (pillRect.left >= hostRect.left && pillRect.right <= hostRect.right) return; // already fully visible
        const delta = (pillRect.left + pillRect.width / 2) - (hostRect.left + hostRect.width / 2);
        host.scrollLeft += delta; // browser clamps to [0, maxScroll]; scrolls only the row, not the page
      });
    }

    selectSize(size) {
      if (!this.state.format) return;
      if (!this.formatIndex[this.state.format][size]) return;
      this.state.size = size;
      this.markSelectedSizePill(size);
      // For framed formats, if no frame chosen yet, auto-pick first available.
      if (!this.state.frameColor || this.state.frameColor === 'none') {
        const colors = Object.keys(this.formatIndex[this.state.format][size]);
        if (colors.length > 0) {
          if (!this.state.frameColor) this.state.frameColor = colors[0];
          if (!colors.includes(this.state.frameColor)) this.state.frameColor = colors[0];
        }
      }
      this.commit();
    }

    selectFrameColor(frameColor) {
      this.state.frameColor = frameColor;
      this.commit();
    }

    renderFrameSwatches(frameColors) {
      const labels = { black: 'Black', white: 'White', natural_oak: 'Natural Oak' };
      // Direct background values — set as inline `background:` on the swatch
      // span, no CSS variables. Wood-grain for natural oak via gradient.
      const backgrounds = {
        black: '#1a1a1a',
        white: '#f5f5f5',
        natural_oak: 'linear-gradient(135deg, #d4ad7e 0%, #c19a6b 50%, #a87f56 100%)',
      };
      this.frameSwatchesHost.innerHTML = frameColors
        .map((fc) => {
          const label = labels[fc] || fc;
          const bg = backgrounds[fc] || '#999';
          return `
            <button
              type="button"
              class="wap-swatch"
              data-wap-frame="${fc}"
              aria-label="${label}"
              title="${label}"
            >
              <span class="visually-hidden">${label}</span>
              <span class="wap-swatch__color" style="background: ${bg};"></span>
            </button>
          `;
        })
        .join('');
    }

    // ─── Commit to form + price display ─────────────────────────────────

    commit() {
      const { format, size, frameColor } = this.state;
      if (!format || !size || !frameColor) return;

      const lookup = this.formatIndex[format]?.[size]?.[frameColor];
      if (!lookup) {
        console.warn('[wall-art-picker] no pricing_variant_map entry for', { format, size, frameColor });
        return;
      }

      // Update the form's hidden id input.
      if (this.form) {
        const idInput = this.form.querySelector('[name="id"]');
        if (idInput) idInput.value = String(lookup.variant_id);
        this.upsertHiddenProperty('properties[_design_id]', this.designContext.designId);
        // `_design_uuid` carries the internal UUIDv4 (from `designs.id` in
        // Electron's SQLite) which the Worker uses to deterministically
        // compute Google Shopping feed item IDs at conversion time. Skip
        // when blank (legacy products without the `custom.design_id`
        // metafield set — caller can fall back to looking it up via
        // Shopify Admin API). See Phase D.1.a in the plan.
        if (this.designContext.designUuid) {
          this.upsertHiddenProperty('properties[_design_uuid]', this.designContext.designUuid);
        } else {
          this.removeHiddenProperty('properties[_design_uuid]');
        }
        this.upsertHiddenProperty('properties[_design_handle]', this.designContext.designHandle);
        this.upsertHiddenProperty('properties[_design_name]', this.designContext.designName);
        this.upsertHiddenProperty('properties[_design_image]', this.designContext.designImage);
        // Customer-visible design name for the Shopify-hosted CHECKOUT. The
        // underscore-prefixed `_design_name` above is hidden by Shopify, and the
        // cart line's title is swapped to the design name by wall-art-cart-display.js
        // — but checkout doesn't run theme JS, so this `Product: <name>` property is
        // what surfaces the real design there + on the order. Theme cart surfaces
        // skip the `Product` key (see cart-drawer / main-cart-items / cart-notification)
        // so it doesn't duplicate the swapped title.
        this.upsertHiddenProperty('properties[Product]', this.designContext.designName);
        this.upsertHiddenProperty('properties[Format]', this.formatLabel(format));
        this.upsertHiddenProperty('properties[Size]', size);
        if (frameColor && frameColor !== 'none') {
          this.upsertHiddenProperty('properties[Frame Color]', this.frameColorLabel(frameColor));
        } else {
          this.removeHiddenProperty('properties[Frame Color]');
        }
      }

      // Update the displayed price (formatted via shop.money_format).
      this.updateDisplayedPrice(lookup.price);

      // Reflect the current selection in the URL so the page is shareable
      // and the customer can copy/bookmark a specific format/size/frame combo.
      this.writeSelectionToUrl();

      // Persist across product navigation so the next product page restores
      // this format/size/frame instead of the 24x36 Art Print default.
      this.writeStoredSelection();

      // Update frame swatch visual state (selected outline matches Dawn's
      // .swatch-input__input:checked + .swatch-input__label appearance).
      for (const btn of this.querySelectorAll('[data-wap-frame]')) {
        btn.classList.toggle('wap-swatch--selected', btn.dataset.wapFrame === frameColor);
      }
      if (this.frameSelectedLabel && frameColor && frameColor !== 'none') {
        this.frameSelectedLabel.textContent = this.frameColorLabel(frameColor);
      }

      // Notify other components (gallery renderer, anything custom).
      const detail = {
        format, size, frameColor,
        variantId: lookup.variant_id,
        price: lookup.price,
      };
      // Also store on a window global so components that init AFTER the
      // picker's first emit (e.g. wall-art-gallery-renderer.js loaded with
      // `defer`) can pick up the current state on their first run.
      window.__wallArtCurrentSelection = detail;
      this.dispatchEvent(new CustomEvent('wallart:selection:changed', {
        bubbles: true,
        detail,
      }));
    }

    /** Phase D.2 — fire the GA4-style `view_item` event on initial page
     *  load. Uses the picker's current state (already set by selectFormat
     *  from URL params or first-format defaults). Skipped silently when
     *  the YukiConversionTracker module hasn't loaded (defer race). */
    fireViewItem() {
      const t = window.YukiConversionTracker;
      if (!t || typeof t.viewItem !== 'function') return;
      if (!this.state || !this.state.format) return;
      // Variant lookup mirrors selectFormat's pattern at line ~389: walk
      // the prebuilt formatIndex by (format → size → frameColor) to get
      // { variant_id, price }. Returns undefined for unknown tuples.
      const lookup = this.formatIndex[this.state.format]?.[this.state.size]?.[this.state.frameColor];
      const ctx = this.designContext || {};
      t.viewItem({
        item_id_override: null,
        design_uuid: ctx.designUuid || '',
        design_handle: ctx.designHandle || '',
        design_name: ctx.designName || '',
        design_image: ctx.designImage || '',
        format: this.state.format,
        size: this.state.size,
        frame_color: this.state.frameColor,
        shopify_product_id: ctx.designId || null,
        price: lookup ? lookup.price : 0,
        quantity: 1,
        category: 'Wall Art',
      });
    }

    /** Phase D.2 — fire `add_to_cart` after the product-form successfully
     *  posts to /cart/add.js. We subscribe to the `cart-update` pub/sub
     *  in connectedCallback; this handler ignores events that don't
     *  match this picker's resolved variant id (defensive — quick-add
     *  modals etc. can emit cart-update from other forms). */
    fireAddToCart(eventDetail) {
      const t = window.YukiConversionTracker;
      if (!t || typeof t.addToCart !== 'function') return;
      if (!this.state || !this.state.format) return;
      const lookup = this.formatIndex[this.state.format]?.[this.state.size]?.[this.state.frameColor];
      if (!lookup) return;
      // Only emit if the cart-update event corresponds to OUR submission.
      // product-form.js publishes { productVariantId } — match against
      // the variant we just resolved.
      if (eventDetail && eventDetail.productVariantId
          && String(eventDetail.productVariantId) !== String(lookup.variant_id)) {
        return;
      }
      const ctx = this.designContext || {};
      t.addToCart({
        items: [{
          design_uuid: ctx.designUuid || '',
          design_handle: ctx.designHandle || '',
          design_name: ctx.designName || '',
          design_image: ctx.designImage || '',
          format: this.state.format,
          size: this.state.size,
          frame_color: this.state.frameColor,
          shopify_product_id: ctx.designId || null,
          price: lookup.price,
          quantity: 1,
          category: 'Wall Art',
        }],
      });
    }

    upsertHiddenProperty(name, value) {
      let input = this.form.querySelector(`input[name="${name}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        this.form.appendChild(input);
      }
      input.value = value ?? '';
    }

    removeHiddenProperty(name) {
      const input = this.form.querySelector(`input[name="${name}"]`);
      if (input) input.remove();
    }

    updateDisplayedPrice(price) {
      // The price-related elements live outside this picker. The simplest
      // robust approach: find the page's `.price__sale .price-item--regular`
      // and `.price-item.price-item--regular` and overwrite their text.
      // We intentionally don't try to be too clever here — themes vary.
      const formatted = this.formatMoney(price);
      const targets = document.querySelectorAll([
        '.price__regular .price-item--regular',
        '.price-item.price-item--regular',
        '.price__sale .price-item--regular',
        '.price-item.price-item--sale',
      ].join(','));
      for (const t of targets) {
        t.textContent = formatted;
      }
    }

    formatMoney(amount) {
      // Minimal money formatter that respects shop.money_format if it follows
      // the {{amount}} or {{amount_no_decimals}} pattern. Anything weirder,
      // fall back to a simple "$XX.XX".
      const fmt = this.dataset.moneyFormat || '${{amount}}';
      const fixed = amount.toFixed(2);
      const noDecimals = Math.round(amount).toString();
      return fmt
        .replace('{{amount}}', fixed)
        .replace('{{amount_no_decimals}}', noDecimals)
        .replace('{{amount_with_comma_separator}}', fixed.replace('.', ','))
        .replace('{{amount_no_decimals_with_comma_separator}}', noDecimals);
    }

    formatLabel(format) {
      return ({
        art_print: 'Art Print',
        framed_art_print: 'Framed Art Print',
        canvas_print: 'Canvas Print',
        framed_canvas_print: 'Framed Canvas Print',
      })[format] || format;
    }

    frameColorLabel(fc) {
      return ({ black: 'Black', white: 'White', natural_oak: 'Natural Oak' })[fc] || fc;
    }
  }

  customElements.define('wall-art-picker', WallArtPicker);
}
